/**
 * @fileoverview Underlined text-link button atom.
 * @description A `<button>` styled to read as an inline text link (underlined) for actions that
 * aren't a primary submit — e.g. opening a secondary flow, signing out. Explicit cursor-pointer,
 * since native `<button>` elements don't get a pointer cursor by default the way `<a>` elements
 * do. Text sizes come from the shared `--text-*` tokens in src/styles/tokens/typography.css,
 * selected via a size prop; color is a tone prop — both closed variant props rather than an
 * overridable className, matching every other atom.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface LinkButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'type'
> {
  size?: 'xs' | 'sm' | 'md'
  tone?: 'primary' | 'footer'
}

const SIZE_CLASS_NAMES: Record<NonNullable<LinkButtonProps['size']>, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-md',
}

const TONE_CLASS_NAMES: Record<NonNullable<LinkButtonProps['tone']>, string> = {
  primary: 'text-primary',
  footer: 'text-footer',
}

/**
 * @component
 * @param {LinkButtonProps} props - Standard button attributes (minus type) plus size/tone.
 * @param {'xs' | 'sm' | 'md'} [props.size] - "md" (default, 15px), "sm" (14px), or "xs" (12px,
 * e.g. the page footer's Privacy Policy/Terms of Service/Contact Support links).
 * @param {'primary' | 'footer'} [props.tone] - "primary" (default, brand blue) or "footer" (the
 * page footer's grey, e.g. its Privacy Policy/Terms of Service/Contact Support links).
 */
function LinkButton({ size = 'md', tone = 'primary', ...props }: LinkButtonProps) {
  return (
    <button
      type="button"
      className={`cursor-pointer font-medium underline ${TONE_CLASS_NAMES[tone]} ${SIZE_CLASS_NAMES[size]}`}
      {...props}
    />
  )
}

export default LinkButton
